import mixpanel from 'mixpanel-browser';
import Settings from './settings';
import {environmentName} from './Helpers/EndPointHelpers';

const currentEnvironment = environmentName();
const mixpanelApiKey = Settings.mixpanelApiKey;
const PLATFORM = 'hsc_web';
const APPLICATION = 'hsc';
const SESSION_ID_KEY = 'hsc.analytics.session_id';
const SESSION_EXPIRES_AT_KEY = 'hsc.analytics.session_expires_at';
const SAFE_PROPERTY_NAMES = new Set(['flow_name', 'step_name', 'button_id', 'target', 'previous_page']);
const ALLOWED_EVENT_NAMES = new Set(['page.viewed', 'button.clicked', 'flow.started', 'flow.completed', 'flow.abandoned']);

let hsFeatures = [];
let hasInitializedMixPanel = false;
let activeSession = null;

function logToConsole(message){
  if(!currentEnvironment.includes('Production')){
    console.log(message);
  }
}

function hsFeatureAvailable(featureName){
  return hsFeatures.some((feature) => feature.title === featureName && feature.enabled);
}

function storage(){
  try{
    return window.sessionStorage;
  }
  catch(error){
    return null;
  }
}

function createIdentifier(prefix){
  if(window.crypto && window.crypto.getRandomValues){
    const values = new Uint32Array(4);
    window.crypto.getRandomValues(values);
    return prefix + '-' + Array.from(values).map((value) => value.toString(16)).join('');
  }
  return prefix + '-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
}

function validSession(session){
  return session && validIdentifier(session.id) && normalizedExpiry(session.expiresAt);
}

function validIdentifier(value){
  return typeof value === 'string' && value.length > 0 && value.length <= 128 && /^[A-Za-z0-9._:-]+$/.test(value);
}

function normalizedExpiry(value){
  const expiresAt = new Date(value);
  if(Number.isNaN(expiresAt.getTime()) || expiresAt.getTime() <= Date.now()){
    return null;
  }
  return expiresAt.toISOString();
}

function readSession(){
  if(validSession(activeSession)){
    return activeSession;
  }
  const sessionStorage = storage();
  if(sessionStorage){
    const storedSession = {id:sessionStorage.getItem(SESSION_ID_KEY), expiresAt:sessionStorage.getItem(SESSION_EXPIRES_AT_KEY)};
    if(validSession(storedSession)){
      activeSession = storedSession;
      return activeSession;
    }
    sessionStorage.removeItem(SESSION_ID_KEY);
    sessionStorage.removeItem(SESSION_EXPIRES_AT_KEY);
  }
  activeSession = null;
  return null;
}

function persistSession(session){
  activeSession = {id:session.id, expiresAt:normalizedExpiry(session.expiresAt)};
  const sessionStorage = storage();
  if(sessionStorage){
    sessionStorage.setItem(SESSION_ID_KEY, activeSession.id);
    sessionStorage.setItem(SESSION_EXPIRES_AT_KEY, activeSession.expiresAt);
  }
}

function requestHeaders(context = {}){
  const session = readSession();
  const headers = {};
  if(session && !context.withoutSession){ headers['X-ANALYTICS-SESSION-ID'] = session.id; }
  if(context.flowId){ headers['X-ANALYTICS-FLOW-ID'] = context.flowId; }
  if(context.correlationId){ headers['X-ANALYTICS-CORRELATION-ID'] = context.correlationId; }
  if(context.page){ headers['X-ANALYTICS-PAGE'] = context.page; }
  return headers;
}

function canonicalEventName(name){
  return String(name || '').trim().toLowerCase().replace(/[^a-z0-9_]+/g, '.').replace(/^\.|\.$/g, '');
}

function pagePath(value){
  const path = String(value || window.location.pathname || '').split('?')[0].split('#')[0];
  return path.replace(/[^a-zA-Z0-9/_-]/g, '').slice(0, 512) || '/';
}

function safeProperties(properties){
  if(!properties || typeof properties !== 'object'){
    return {};
  }
  return Object.keys(properties).reduce((result, key) => {
    const value = properties[key];
    if(!SAFE_PROPERTY_NAMES.has(key) || !['string', 'number', 'boolean'].includes(typeof value)){
      return result;
    }
    result[key] = String(value).slice(0, 128);
    return result;
  }, {});
}

function postAnalytics(name, properties = {}, context = {}){
  const session = readSession();
  const eventName = canonicalEventName(name);
  if(!session || !ALLOWED_EVENT_NAMES.has(eventName) || !global.api){
    return Promise.resolve();
  }
  const eventContext = {
    flow_id:context.flowId,
    correlation_id:context.correlationId,
    page:pagePath(context.page),
    screen:context.screen,
    action:context.action,
    outcome:context.outcome
  };
  const event = Object.keys(eventContext).reduce((result, key) => {
    if(eventContext[key]){
      result[key] = eventContext[key];
    }
    return result;
  }, {
    event_id:createIdentifier('hsc-event'),
    name:eventName,
    occurred_at:new Date().toISOString(),
    platform:PLATFORM,
    application:APPLICATION,
    properties:safeProperties(properties)
  });

  return global.api.post('/api/analytics_events', {event}, {
    withCredentials:true,
    headers:requestHeaders({...context, page:event.page})
  }).then(() => {
    logToConsole('Analytics Event (' + eventName + ')');
  }).catch((error) => {
    if(error && error.response && error.response.status === 419){
      EventTracker.clearSession();
    }
    logToConsole('Analytics Event Failure: ' + error);
  });
}

function initializeMixPanel(){
  if(!hasInitializedMixPanel && hsFeatureAvailable('SendToMixPanel')){
    hasInitializedMixPanel = true;
    mixpanel.init(mixpanelApiKey);
  }
}

const EventTracker = {
  identify:(id, features) => {
    hsFeatures = features || [];
    initializeMixPanel();
    if(hasInitializedMixPanel && id){
      mixpanel.identify(id);
    }
  },
  updateHsFeatures:(features) => {
    hsFeatures = features || [];
    initializeMixPanel();
  },
  setTokenAndJWT:() => {},
  setCompanyId:() => {},
  prepareAuthentication:(flowName) => {
    const name = canonicalEventName(flowName || 'sign_in');
    const flowId = createIdentifier('hsc-flow-' + name);
    const correlationId = createIdentifier('hsc-correlation');
    return {
      flowId,
      correlationId,
      method:name,
      name,
      headers:requestHeaders({flowId, correlationId, page:'/login', withoutSession:true})
    };
  },
  completeAuthentication:(response, flow) => {
    const headers = (response && response.headers) || {};
    const responseSession = headers['x-analytics-session-id'] || headers['X-ANALYTICS-SESSION-ID'];
    const responseExpiry = headers['x-analytics-session-expires-at'] || headers['X-ANALYTICS-SESSION-EXPIRES-AT'];
    if(!responseSession && !responseExpiry){
      return Boolean(readSession());
    }
    const session = {
      id:responseSession,
      expiresAt:normalizedExpiry(responseExpiry)
    };
    if(!validSession(session)){
      EventTracker.clearSession();
      return false;
    }
    persistSession(session);
    if(flow && flow.flowId){
      EventTracker.track('flow.started', {flow_name:flow.name}, {
        flowId:flow.flowId,
        correlationId:flow.correlationId,
        page:'/login'
      });
      EventTracker.buttonClicked(flow.name + '_submit', {
        flowId:flow.flowId,
        correlationId:flow.correlationId,
        page:'/login',
        action:'submit'
      });
      EventTracker.finishFlow(flow, 'completed', {page:'/login', outcome:'success'});
    }
    return true;
  },
  clearSession:() => {
    activeSession = null;
    const sessionStorage = storage();
    if(sessionStorage){
      sessionStorage.removeItem(SESSION_ID_KEY);
      sessionStorage.removeItem(SESSION_EXPIRES_AT_KEY);
    }
  },
  analyticsHeaders:(context = {}) => requestHeaders({...context, page:pagePath(context.page)}),
  track:(name, properties, context) => {
    const eventName = canonicalEventName(name);
    const tracking = postAnalytics(eventName, properties, context);
    if(hasInitializedMixPanel && eventName){
      mixpanel.track(eventName, safeProperties(properties));
    }
    return tracking;
  },
  pageViewed:(page) => EventTracker.track('page.viewed', {}, {page:pagePath(page)}),
  buttonClicked:(button, context = {}) => EventTracker.track('button.clicked', {button_id:button}, context),
  startFlow:(name, context = {}) => {
    const flow = {
      flowId:createIdentifier('hsc-flow-' + canonicalEventName(name)),
      correlationId:createIdentifier('hsc-correlation'),
      name:canonicalEventName(name)
    };
    EventTracker.track('flow.started', {flow_name:flow.name}, {...context, flowId:flow.flowId, correlationId:flow.correlationId});
    return flow;
  },
  finishFlow:(flow, outcome = 'completed', context = {}) => {
    if(flow && flow.flowId && ['completed', 'abandoned'].includes(outcome)){
      EventTracker.track('flow.' + outcome, {flow_name:flow.name}, {
        ...context,
        flowId:flow.flowId,
        correlationId:flow.correlationId,
        outcome
      });
    }
  },
  endSession:() => EventTracker.clearSession(),
  error:(name, error) => {
    logToConsole('Client Error (' + name + '): ' + error);
  }
};

export default EventTracker;
